import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { MemberPartialDto, MembersFieldDto } from "../../../types/membersTypes";
import MemberSelection from "../../general/MemberSelection";
import { CreateInternalProjectDto } from "../../../types/traineesTypes";

interface AddInternalProjectDialogProps {
  open: boolean;
  handleDialogClose: () => void;
  addInternalProject: (internalProjectData: CreateInternalProjectDto) => Promise<void>;
  members: MemberPartialDto[];
  generationId: number;
  generationName: string;
  trainees: MembersFieldDto[];
}

/**
 * Dialog to add an internal project to the database
 * @param open - boolean to determine if the dialog is open
 * @param handleDialogClose - function to close the dialog
 * @param addInternalProject - function to add the internal project to the database
 * @param members - array of all members who can be selected as QMs
 * @param generationId - id of the generation to which the internal project belongs
 * @param generationName - name of the generation
 * @param trainees - array of all trainees who can be selected as project members
 * @returns a dialog to add an internal project to the database
 */
const AddInternalProjectDialog: React.FunctionComponent<AddInternalProjectDialogProps> = ({
  open,
  handleDialogClose,
  addInternalProject,
  members,
  generationId,
  generationName,
  trainees,
}: AddInternalProjectDialogProps) => {
  const [projectName, setProjectName] = useState<string>("");
  const [projectShort, setProjectShort] = useState<string>("");

  const [selectedMembers, setSelectedMembers] = useState<MembersFieldDto[]>([]);
  const [selectedQMs, setSelectedQMs] = useState<MembersFieldDto[]>([]);

  const [projectNameError, setProjectNameError] = useState<boolean>(false);
  const [projectShortError, setProjectShortError] = useState<boolean>(false);

  const [errorEmptyMembers, setErrorEmptyMembers] = useState<boolean>(false);
  const [errorEmptyQMs, setErrorEmptyQMs] = useState<boolean>(false);

  // selectableQMs are all members who are not trainees or have left the organization
  const selectableQMs: MembersFieldDto[] = members
    .filter((member) => member.memberStatus?.name === "aktives Mitglied" || member.memberStatus?.name === "Senior")
    .map((member) => ({
      memberId: member.memberId,
      firstname: member.firstname,
      lastname: member.lastname,
      memberStatus: member.memberStatus,
    }));

  // reset all values when dialog is closed
  const handleClose = () => {
    handleDialogClose();
    setSelectedQMs([]);
    setSelectedMembers([]);
    setProjectName("");
    setProjectShort("");
    setProjectNameError(false);
    setProjectShortError(false);
    setErrorEmptyMembers(false);
    setErrorEmptyQMs(false);
  };

  /**
   * Checks if the form is valid and sets the respective error states
   * @returns boolean - true if the form is valid, false otherwise
   */
  const checkIsFormValid = (): boolean => {
    let isValid = false;
    // check if the project name is not empty and does not exceed 150 characters
    if (projectName.trim() === "" || projectName.length > 150) {
      setProjectNameError(true);
    }
    // check if the project short is not empty and does not exceed 10 characters
    if (projectShort.trim() === "" || projectShort.length > 10) {
      setProjectShortError(true);
    }
    // check if there are empty members
    if (selectedMembers.some((member) => Object.keys(member).length === 0)) {
      setErrorEmptyMembers(true);
    }
    // check if there are empty qms
    if (selectedQMs.some((member) => Object.keys(member).length === 0)) {
      setErrorEmptyQMs(true);
    }
    // if there are no errors, the form is valid
    if (
      projectName.trim() !== "" &&
      projectName.length <= 150 &&
      projectShort.trim() !== "" &&
      projectShort.length <= 10 &&
      !selectedMembers.some((member) => Object.keys(member).length === 0) &&
      !selectedQMs.some((member) => Object.keys(member).length === 0)
    ) {
      isValid = true;
    }
    return isValid;
  };

  // calls the addInternalProject function from the parent component and closes the dialog
  const handleSave = () => {
    if (!checkIsFormValid()) {
      return;
    }

    addInternalProject({
      generationId: generationId,
      projectName: projectName,
      abbreviation: projectShort,
      members: selectedMembers.filter((member) => member.memberId !== undefined),
      qualityManagers: selectedQMs.filter((qm) => qm.memberId !== undefined),
    });
    handleClose();
  };

  // Function to handle the selection of a project member
  const handleMemberSelection = (event: React.SyntheticEvent, value: MembersFieldDto | null) => {
    event.persist();
    const index = Number(event.currentTarget.id.split("-")[1]);
    if (value !== null) {
      const updatedMembers = [...selectedMembers];
      updatedMembers[index] = value;
      setSelectedMembers(updatedMembers);
    }
  };

  // Function to add a project member to the list of members
  const addMember = () => {
    setSelectedMembers((prev) => [...prev, [] as unknown as MembersFieldDto]);
  };

  // Function to remove a project member from the list of members
  const removeMember = (memberId: number) => {
    const updatedMembers = selectedMembers.filter((member) => member.memberId !== memberId);
    setSelectedMembers(updatedMembers);
  };

  // Function to handle the selection of qms
  const handleQMSelection = (event: React.SyntheticEvent, value: MembersFieldDto | null) => {
    event.persist();
    const index = Number(event.currentTarget.id.split("-")[1]);
    if (value !== null) {
      const updatedQMs = [...selectedQMs];
      updatedQMs[index] = value;
      setSelectedQMs(updatedQMs);
    }
  };

  // Function to add a qm to the list of qms
  const addQM = () => {
    setSelectedQMs((prev) => [...prev, [] as unknown as MembersFieldDto]);
  };

  // Function to remove a qm from the list of qms
  const removeQM = (qmId: number) => {
    const updatedQMs = selectedQMs.filter((qm) => qm.memberId !== qmId);
    setSelectedQMs(updatedQMs);
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Neues internes Projekt erstellen</DialogTitle>
      <DialogContent>
        <Box sx={{ flexGrow: 1, mt: 1 }}>
          <Grid container spacing={1} justifyContent="center">
            <Grid item xs={12} md={3.5} style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              <Typography>Projektname:</Typography>
            </Grid>
            <Grid item xs={12} md={8.5} style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              <TextField
                fullWidth
                id="name"
                error={projectNameError}
                helperText={projectNameError ? "Projektname ist erforderlich" : ""}
                variant="outlined"
                size="small"
                value={projectName}
                onChange={(e) => {
                  setProjectName(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} md={3.5} style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              <Typography>Kürzel:</Typography>
            </Grid>
            <Grid item xs={12} md={8.5} style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              <TextField
                fullWidth
                id="kuerzel"
                error={projectShortError}
                helperText={projectShortError ? "Kürzel ist erforderlich" : ""}
                variant="outlined"
                size="small"
                value={projectShort}
                onChange={(e) => {
                  setProjectShort(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} md={3.5} style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              <Typography>Traineegeneration:</Typography>
            </Grid>
            <Grid item xs={12} md={8.5} style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              <TextField
                fullWidth
                disabled
                id="traineegeneration"
                variant="outlined"
                size="small"
                defaultValue={generationName}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 1 }}>
          <Typography sx={{ mb: 1 }}>Projektmitglieder:</Typography>
          {errorEmptyMembers ? (
            <Typography color="error" sx={{ mb: 1 }}>
              Bitte füge ein Projektmitglied hinzu oder lösche das leere Feld
            </Typography>
          ) : null}
          <MemberSelection
            selectedMembers={selectedMembers}
            selectableMembers={trainees}
            onChangeCallback={handleMemberSelection}
            addMember={addMember}
            removeMember={removeMember}
          />
        </Box>

        <Box sx={{ mt: 1 }}>
          <Typography sx={{ mb: 1 }}>Qualitätsmanager:</Typography>
          {errorEmptyQMs ? (
            <Typography color="error" sx={{ mb: 1 }}>
              Bitte füge ein Qualitätsmanager hinzu oder lösche das leere Feld
            </Typography>
          ) : null}
          <MemberSelection
            selectedMembers={selectedQMs}
            selectableMembers={selectableQMs}
            onChangeCallback={handleQMSelection}
            addMember={addQM}
            removeMember={removeQM}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Abbrechen</Button>
        <Button onClick={handleSave}>Speichern</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddInternalProjectDialog;
